import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Delete,
  Edit,
  InfoOutlined,
  InventoryOutlined,
  MoreVert,
  PersonOutlined,
  PinDropOutlined,
  RadarOutlined,
  Settings,
  StorageOutlined,
} from "@mui/icons-material";
import {
  createOpcaoCircuito,
  updateOpcaoCircuito,
  deleteOpcaoCircuito,
} from "../opcaoCircuitoService";
import useEscalationContacts from "../hooks/useEscalationContacts";
import RedeSection from "../formSections/RedeSection";
import VelocidadeSection from "../formSections/VelocidadeSection";
import ParceiroUltimaMilhaSection from "../formSections/ParceiroUltimaMilhaSection";
import StatusTags from "../../../../../../components/uniqueTagSelect";
import InternoModalConfirmacaoSaida from "../../../../../../components/modalSaindoDaPagina";
import OpcoesEquipamento from "./OpcoesEquipamento";
import { ModalEditCheckbox } from "../ModalEditCheckbox";
import labelCircuito from "../../labelCircuito";
import useStyles from "./styles";

// Função auxiliar para processar arrays (merge de array por optionId); só usada
// pelo efeito de sincronização de serviceTypes/deliveryMedium abaixo.
const processArray = (prevArray, newArray, type) => {
  // Criar mapa dos novos itens por optionId para acesso rápido
  const newItemsMap = new Map();
  newArray?.forEach((item) => {
    newItemsMap?.set(item?.optionId, item);
  });

  // Processar itens existentes
  const updatedArray =
    prevArray
      ?.filter((item) => newItemsMap?.has(item?.optionId)) // Manter apenas os que existem no delivery
      ?.map((item) => {
        const newItem = newItemsMap?.get(item?.optionId);
        // Atualizar apenas o label se o item já existia
        return {
          ...item,
          label: newItem?.label,
        };
      }) || [];

  // Adicionar novos itens que não existiam anteriormente
  newArray?.forEach((item) => {
    if (!prevArray?.some((prevItem) => prevItem?.optionId === item?.optionId)) {
      updatedArray?.push(item);
    }
  });

  return updatedArray;
};

// Seções Rede, Tipos de Entrega, Cliente Final, Parceiro, Velocidade, POP e
// Regional, Equipamentos e Meio de Entrega do formulário de circuito: fronteira
// que a etapa de hierarquia (fora deste plano) vai transformar na aba
// "Formulário" de fato — hoje ainda renderizada empilhada na mesma ordem visual
// de antes. Extraída de circuito/index.js (Task 6 da decomposição), mesmo
// JSX/comportamento.
export default function FormularioTab({
  index,
  formData,
  setFormData,
  setPrevData,
  errors,
  handleChange,
  handleNestedChange,
  validateBlocoAndIp,
  temVelocidade,
  editMode,
  loading,
  setLoading,
  loadingSerial,
  delivery,
  setChanged,
  showAllContacts,
  setShowAllContacts,
  serviceId,
  clientCpes,
  optionsRoteamento,
  setOptionsRoteamento,
  optionsEntregaFinal,
  setOptionsEntregaFinal,
  optionsEntregaFinalCliente,
  setOptionsEntregaFinalCliente,
  valueRoteamento,
  setValueRoteamento,
  valueEntregaFinal,
  setValueEntregaFinal,
  valueEntregaFinalCliente,
  setValueEntregaFinalCliente,
  updateOptions,
  setSnackbar,
  popTags,
  regionalTags,
  loadingTags,
  selectedPopId,
  handlePopSelect,
  handlePopCreate,
  selectedRegionalId,
  handleRegionalSelect,
  handleRegionalCreate,
  handleTagUpdate,
  handleTagDelete,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  // Estado para armazenar os hosts CPE (derivado da prop clientCpes já recebida
  // por este componente, em vez de duplicar o cálculo em circuito/index.js)
  const hostsCPE = clientCpes?.[serviceId] || [];

  // Estado para controlar a abertura do menu de configuração (Tipos de
  // Entrega/Meio de Entrega)
  const [loadingDeliveryTypes, setLoadingDeliveryTypes] = useState(false);
  const [selectedFieldItem, setSelectedFieldItem] = useState(null);
  const [anchorActionMenu, setAnchorActionMenu] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedLabel, setEditedLabel] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const {
    handleAddContatoEscalacao,
    handleRemoveContatoEscalacao,
    handleContatoEscalacaoChange,
  } = useEscalationContacts({
    formData,
    setFormData,
    setChanged,
    setShowAllContacts,
  });

  useEffect(() => {
    if (!delivery?.serviceTypes && !delivery?.deliveryMedium) return;

    setPrevData((prev) => {
      return {
        ...prev,
        serviceTypes: processArray(
          prev?.serviceTypes,
          delivery?.serviceTypes,
          "serviceTypes",
        ),
        deliveryMedium: processArray(
          prev?.deliveryMedium,
          delivery?.deliveryMedium,
          "deliveryMedium",
        ),
      };
    });
    setFormData((prev) => {
      return {
        ...prev,
        serviceTypes: processArray(
          prev?.serviceTypes,
          delivery?.serviceTypes,
          "serviceTypes",
        ),
        deliveryMedium: processArray(
          prev?.deliveryMedium,
          delivery?.deliveryMedium,
          "deliveryMedium",
        ),
      };
    });
    // setFormData/setPrevData vêm de useCircuitoForm mas são setters de useState,
    // logo têm identidade estável — inclusão aqui só satisfaz o lint, sem mudar
    // quando o efeito dispara (continua só quando `delivery` muda)
  }, [delivery, setFormData, setPrevData]);

  // Função para abrir o menu
  const handleMenuClick = (event) => {
    setEditingField(event.currentTarget.name);
    setAnchorEl(event.currentTarget);
  };

  // Função para fechar o menu
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  // Função para salvar a edição
  async function handleSaveEditCheckbox() {
    try {
      setLoadingDeliveryTypes(true);
      setLoading(true);
      if (
        editedLabel?.trim() &&
        !formData?.[editingField]?.some(
          (type) =>
            type?.label?.toLowerCase() === editedLabel?.trim()?.toLowerCase(),
        )
      ) {
        await updateOpcaoCircuito({
          id: selectedFieldItem?.optionId,
          label: editedLabel,
        });

        setFormData((prev) => {
          const prevArray = prev?.[editingField] ?? [];
          const newArray = prevArray?.map((item, index) =>
            index === selectedFieldItem?.index
              ? { ...item, label: editedLabel }
              : item,
          );
          return {
            ...prev,
            [editingField]: newArray,
          };
        });
        setPrevData((prev) => {
          const prevArray = prev?.[editingField] ?? [];
          const newArray = prevArray?.map((item, index) =>
            index === selectedFieldItem?.index
              ? { ...item, label: editedLabel }
              : item,
          );
          return {
            ...prev,
            [editingField]: newArray,
          };
        });
        setEditModalOpen(false);
        setSnackbar({
          children: "Seletor atualizado com sucesso",
          severity: "success",
        });
        updateOptions();
      } else {
        throw new Error("Titulo precisa ser único");
      }
    } catch (error) {
      console.error(error);
      if (error?.message === "Titulo precisa ser único") {
        setSnackbar({
          children: "Titulo precisa ser único",
          severity: "error",
        });
      } else {
        setSnackbar({
          children: "Falha ao editar seletor",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
      setLoadingDeliveryTypes(false);
    }
  }

  async function handleRemoveCheckbox() {
    try {
      setLoadingDeliveryTypes(true);
      setLoading(true);
      await deleteOpcaoCircuito(selectedFieldItem?.optionId);
      setFormData((prev) => ({
        ...prev,
        [editingField]: prev?.[editingField]?.filter(
          (_, i) => i !== selectedFieldItem?.index,
        ),
      }));
      setPrevData((prev) => ({
        ...prev,
        [editingField]: prev?.[editingField]?.filter(
          (_, i) => i !== selectedFieldItem?.index,
        ),
      }));
      setSearchTerm("");
      setOpenDeleteModal(false);
      setSnackbar({
        children: "Seletor removido com sucesso",
        severity: "success",
      });
      updateOptions();
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Falha ao remover seletor",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setLoadingDeliveryTypes(false);
    }
  }

  async function handleAddCeckbox(newLabel) {
    try {
      setLoadingDeliveryTypes(true);
      setLoading(true);
      const criado = await createOpcaoCircuito({
        label: newLabel,
        type: `${editingField}`,
      });

      setFormData((prev) => ({
        ...prev,
        [editingField]: [...prev?.[editingField], criado],
      }));
      setPrevData((prev) => ({
        ...prev,
        [editingField]: [...prev?.[editingField], criado],
      }));
      setSearchTerm("");
      setSnackbar({
        children: "Seletor criado com sucesso",
        severity: "success",
      });
      updateOptions();
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Falha ao criar seletor",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setLoadingDeliveryTypes(false);
    }
  }

  // Filtra baseado no termo de busca
  const filteredDeliveryTypes = formData?.[editingField]?.filter((type) =>
    type?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
  );

  // Verifica se existe um match exato com a busca
  const exactMatch = formData?.[editingField]?.some(
    (type) => type?.label?.toLowerCase() === searchTerm?.toLowerCase(),
  );

  // Funções para controlar o menu de ações (MoreVert)
  const handleActionMenuClick = (event, index) => {
    event.stopPropagation();
    setAnchorActionMenu(event.currentTarget);
    setEditingIndex(index);
  };

  const handleActionMenuClose = () => {
    setAnchorActionMenu(null);
    setEditingIndex(null);
  };

  // Função para abrir o modal de edição
  const handleOpenEditModal = () => {
    setEditedLabel(formData?.[editingField][editingIndex]?.label);
    setEditModalOpen(true);
    handleActionMenuClose();
  };

  return (
    <>
      {/* Nome próprio do circuito; vazio mantém o rótulo genérico por posição */}
      <Grid item xs={12} sm={6}>
        <TextField
          sx={styles.textField}
          disabled={!editMode || loading}
          autoComplete="off"
          fullWidth
          size="small"
          label="Nome do circuito"
          placeholder={labelCircuito({}, index)}
          value={formData?.nome ?? ""}
          name="nome"
          onChange={handleChange}
        />
      </Grid>
      <RedeSection
        styles={styles}
        formData={formData}
        editMode={editMode}
        loading={loading}
        loadingSerial={loadingSerial}
        errors={errors}
        handleChange={handleChange}
        validateBlocoAndIp={validateBlocoAndIp}
        setSnackbar={setSnackbar}
      />
      <Grid item xs={12}>
        <Box sx={styles.boxFieldWithConfig}>
          {!!loadingDeliveryTypes && (
            <LinearProgress
              sx={{ ...styles.linearProgress, mt: -2, left: 0 }}
            />
          )}

          <Box sx={styles.titleWithConfig}>
            <InventoryOutlined color="primary" />
            <Typography variant="mySubtitle2">Tipos de Entrega</Typography>
            <Tooltip title="Entregas lógicas: MPLS, L2, entre outras." arrow>
              <InfoOutlined
                sx={{
                  color: theme.palette.tertiary.light,
                  fontSize: 18,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </Box>
          <IconButton
            name="serviceTypes"
            sx={{ my: -1 }}
            onClick={handleMenuClick}
            disabled={!editMode || loading}
          >
            <Settings />
          </IconButton>
        </Box>
      </Grid>
      <Grid container spacing={1} sx={styles.contentIdentation}>
        {!formData?.serviceTypes?.length ? (
          <Grid item xs={12}>
            <Typography
              variant="mySubtitle2"
              color={theme?.palette?.colors?.gray?.medium}
            >
              As opções de tipo de entrega serão exibidas aqui. É necessário
              criar clicando no ícone de configuração ({" "}
              <Settings sx={styles.noDeliveryFound} />
              ).
            </Typography>
          </Grid>
        ) : (
          formData?.serviceTypes?.map((type, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              lg={2}
              key={type?.name}
              sx={styles.formControlItems}
            >
              <FormControlLabel
                disabled={!editMode || loading}
                name={`serviceTypes.${index}`}
                sx={styles.limitWidthCheckbox}
                control={
                  <Checkbox checked={type?.value} onChange={handleChange} />
                }
                label={type?.label}
              />
            </Grid>
          ))
        )}
      </Grid>

      <ParceiroUltimaMilhaSection
        styles={styles}
        formData={formData}
        editMode={editMode}
        loading={loading}
        handleNestedChange={handleNestedChange}
        showAllContacts={showAllContacts}
        setShowAllContacts={setShowAllContacts}
        handleAddContatoEscalacao={handleAddContatoEscalacao}
        handleContatoEscalacaoChange={handleContatoEscalacaoChange}
        handleRemoveContatoEscalacao={handleRemoveContatoEscalacao}
      />
      <VelocidadeSection
        styles={styles}
        formData={formData}
        setFormData={setFormData}
        editMode={editMode}
        loading={loading}
        hostsCPE={hostsCPE}
        handleNestedChange={handleNestedChange}
        temVelocidade={temVelocidade}
      />
      <Grid item xs={12}>
        <Box sx={{ ...styles.boxRow, gap: 2 }}>
          <Box sx={styles.titleWithConfig}>
            <PersonOutlined color="primary" />
            <Typography variant="mySubtitle2">Cliente Final</Typography>
            <Tooltip
              title="Aplicável quando somos o last mile do parceiro, sendo responsáveis pela entrega até o cliente final."
              arrow
            >
              <InfoOutlined
                sx={{
                  color: theme.palette.tertiary.light,
                  fontSize: 18,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </Box>
          <FormControlLabel
            disabled={!editMode || loading}
            size="small"
            control={
              <Switch
                size="small"
                checked={formData?.endClient?.endClientEnabled}
                onChange={(e) =>
                  handleNestedChange(
                    "endClient",
                    "endClientEnabled",
                    e.target.checked,
                  )
                }
                name="endClient.endClientEnabled"
              />
            }
            label={null}
          />
        </Box>
      </Grid>
      <Grid
        container
        spacing={1}
        sx={{
          ...styles.contentIdentation,
          my: formData?.endClient?.endClientEnabled ? 0 : -1,
        }}
      >
        <Collapse
          sx={{
            ...styles.colapse,
          }}
          in={formData?.endClient?.endClientEnabled}
        >
          <Grid item xs={12} sm={6}>
            <TextField
              sx={styles.textField}
              disabled={!editMode || loading}
              autoComplete="off"
              fullWidth
              size="small"
              label="Nome do cliente"
              value={formData?.endClient?.endClientName}
              onChange={(e) =>
                handleNestedChange(
                  "endClient",
                  "endClientName",
                  e.target.value,
                )
              }
            />
          </Grid>
        </Collapse>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} sx={{ ...styles.gritItemTitle }}>
        <PinDropOutlined color="primary" />
        <Typography variant="mySubtitle2">POP e Regional</Typography>
      </Grid>
      <Grid container spacing={1} sx={styles.contentIdentation}>
        <Grid item xs={12} sm={6}>
          <StatusTags
            initialTags={popTags}
            selectedTagId={selectedPopId}
            onTagSelect={handlePopSelect}
            onTagCreate={handlePopCreate}
            onTagUpdate={handleTagUpdate}
            onTagDelete={handleTagDelete}
            texts={{
              label: "POP",
              placeholder: "Selecionar POP",
              searchPlaceholder: "Buscar ou criar  POP",
              editTitle: "Editar POP",
              deleteTitle: "Excluir POP",
              deleteConfirmation: "Tem certeza que deseja excluir a POP",
              nameLabel: "Nome da POP",
              colorLabel: "Cor",
              cancelButton: "Cancelar",
              saveButton: "Salvar",
              deleteButton: "Excluir",
              createButton: 'Criar "{tag}"',
            }}
            loading={loadingTags}
            disabled={!editMode}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatusTags
            initialTags={regionalTags}
            selectedTagId={selectedRegionalId}
            onTagSelect={handleRegionalSelect}
            onTagCreate={handleRegionalCreate}
            onTagUpdate={handleTagUpdate}
            onTagDelete={handleTagDelete}
            texts={{
              label: "Regional",
              placeholder: "Selecionar Regional",
              searchPlaceholder: "Buscar ou criar  Regional",
              editTitle: "Editar Regional",
              deleteTitle: "Excluir Regional",
              deleteConfirmation: "Tem certeza que deseja excluir a Regional",
              nameLabel: "Nome da Regional",
              colorLabel: "Cor",
              cancelButton: "Cancelar",
              saveButton: "Salvar",
              deleteButton: "Excluir",
              createButton: 'Criar "{tag}"',
            }}
            loading={loadingTags}
            disabled={!editMode}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} sx={styles.gritItemTitle}>
        <StorageOutlined color="primary" />
        <Typography variant="mySubtitle2">Equipamentos</Typography>
      </Grid>
      <Grid container spacing={1} sx={styles.contentIdentation}>
        <OpcoesEquipamento
          tipo="roteamento"
          titulo="Roteamento"
          opcoes={optionsRoteamento}
          setOpcoes={setOptionsRoteamento}
          valorSelecionado={valueRoteamento}
          onSelecionar={setValueRoteamento}
          editMode={editMode}
          loading={loading}
          setLoading={setLoading}
          setSnackbar={setSnackbar}
          styles={styles}
        />
        <OpcoesEquipamento
          tipo="entregaFinal"
          titulo="Entrega – Milha Final"
          opcoes={optionsEntregaFinal}
          setOpcoes={setOptionsEntregaFinal}
          valorSelecionado={valueEntregaFinal}
          onSelecionar={setValueEntregaFinal}
          editMode={editMode}
          loading={loading}
          setLoading={setLoading}
          setSnackbar={setSnackbar}
          styles={styles}
        />
        <OpcoesEquipamento
          tipo="entregaFinalCliente"
          titulo="Milha Final (lado cliente)"
          opcoes={optionsEntregaFinalCliente}
          setOpcoes={setOptionsEntregaFinalCliente}
          valorSelecionado={valueEntregaFinalCliente}
          onSelecionar={setValueEntregaFinalCliente}
          editMode={editMode}
          loading={loading}
          setLoading={setLoading}
          setSnackbar={setSnackbar}
          styles={styles}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Box sx={styles.boxFieldWithConfig}>
          {!!loadingDeliveryTypes && (
            <LinearProgress
              sx={{ ...styles.linearProgress, mt: -2, left: 0 }}
            />
          )}
          <Box sx={styles.titleWithConfig}>
            <RadarOutlined color="primary" />
            <Typography variant="mySubtitle2">Meio de Entrega</Typography>
            <Tooltip
              title="Entrega física, podendo ser realizada por: Fibra óptica, Rádio, Cabo AS, entre outras."
              arrow
            >
              <InfoOutlined
                sx={{
                  color: theme.palette.tertiary.light,
                  fontSize: 18,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </Box>
          <IconButton
            name="deliveryMedium"
            sx={{ my: -1 }}
            onClick={handleMenuClick}
            disabled={!editMode || loading}
          >
            <Settings />
          </IconButton>
        </Box>
      </Grid>
      <Grid container spacing={1} sx={styles.contentIdentation}>
        {!formData?.deliveryMedium?.length ? (
          <Grid item xs={12}>
            <Typography
              variant="mySubtitle2"
              color={theme?.palette?.colors?.gray?.medium}
            >
              As opções de meio de entrega serão exibidas aqui. É necessário
              criar clicando no ícone de configuração (
              <Settings sx={styles.noDeliveryFound} />
              ).
            </Typography>
          </Grid>
        ) : (
          formData?.deliveryMedium?.map((type, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              lg={2}
              key={type?.name}
              sx={styles.formControlItems}
            >
              <FormControlLabel
                disabled={!editMode || loading}
                name={`deliveryMedium.${index}`}
                sx={styles.limitWidthCheckbox}
                control={
                  <Checkbox
                    //checked={type?.value?.toLowerCase() === "true"}
                    checked={type?.value}
                    onChange={handleChange}
                  />
                }
                label={type?.label}
              />
            </Grid>
          ))
        )}
      </Grid>

      {/** Menus das checkbox (Tipos de Entrega/Meio de Entrega) */}
      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <Box sx={styles.paperModalEditCheckBoxLabel}>
          <Box sx={styles.paperModalEditCheckBoxStickHeader}>
            {/* Campo de busca/criação */}
            <TextField
              autoComplete="off"
              fullWidth
              size="small"
              placeholder="Buscar ou criar"
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setSearchTerm(e.target.value);
              }}
            />
            <Divider />
          </Box>
          {/* Opção de criar se não houver match exato */}
          {searchTerm && !exactMatch && (
            <MenuItem onClick={() => handleAddCeckbox(searchTerm)}>
              <AddIcon sx={{ mr: 1 }} fontSize="small" />
              <Box sx={styles.editCheckBoxTexts}>Criar "{searchTerm}"</Box>
            </MenuItem>
          )}
          {/* Lista de tipos filtrados */}
          {filteredDeliveryTypes?.map((type, index) => (
            <MenuItem key={index} sx={styles.boxFieldWithConfig}>
              <Box sx={styles.editCheckBoxTexts}>{type?.label}</Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  handleActionMenuClick(e, index);
                  setSelectedFieldItem({ ...type, index: index });
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </MenuItem>
          ))}
        </Box>
      </Menu>
      <Menu
        anchorEl={anchorActionMenu}
        open={Boolean(anchorActionMenu)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={handleOpenEditModal}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenDeleteModal(true);
            handleActionMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <Delete fontSize="small" />
          </ListItemIcon>
          Excluir
        </MenuItem>
      </Menu>
      {/** Modal de edição de label do checkbox (Tipos de Entrega/Meio de Entrega) */}
      <ModalEditCheckbox
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveEditCheckbox}
        title="Editar tipo de entrega"
        value={editedLabel}
        onChange={(e) => setEditedLabel(e.target.value)}
      />
      <InternoModalConfirmacaoSaida
        openConfirmacao={openDeleteModal}
        deleteLoad={loading}
        setOpenConfirmacao={setOpenDeleteModal}
        execute={handleRemoveCheckbox}
        title={"Atenção"}
        severity={"error"}
        contentText={
          "Esta ação afetará TODOS os clientes. Se você prosseguir com a operação, o item será deletado permanentemente!"
        }
        confirmText={"Excluir"}
      />
    </>
  );
}
